import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./webdevelopment.css";
import { companyInfo } from "../../../../CompanyInfo/CompanyInfo";
import {
  Code2, Laptop, Server, ShoppingCart, Layers, Settings,
  Search, FileText, CheckCircle2, ArrowRight, ChevronDown,
  Play, Send, Phone, ShieldCheck, Terminal, Cloud, Database,
  Rocket
} from "lucide-react";

// ─── DATA DEFINITIONS ────────────────────────────────────────────────────────

const stats = [
  { value: "150+", label: "Websites Delivered", icon: <Code2 size={24} />, color: "#2563EB" },
  { value: "99.9%", label: "Uptime Guaranteed", icon: <Cloud size={24} />, color: "#7C3AED" },
  { value: "10+", label: "Tech Stacks Supported", icon: <Terminal size={24} />, color: "#10B981" },
  { value: "100%", label: "Client Satisfaction", icon: <ShieldCheck size={24} />, color: "#F59E0B" },
];

const innerServices = [
  {
    icon: <Laptop size={24} />,
    title: "Frontend Engineering",
    desc: "Interactive, responsive, and ultra-fast user interfaces built with React, Next.js, and modern styling solutions. Optimized for seamless desktop and mobile experiences.",
    color: "#2563EB",
    bg: "#EFF6FF",
    tags: ["React.js", "Next.js", "Tailwind CSS"],
  },
  {
    icon: <Server size={24} />,
    title: "Backend & API Architecture",
    desc: "Robust, secure, and scalable server-side systems. Custom RESTful and GraphQL APIs built using Node.js, Express, Python, or Go to power complex business logic.",
    color: "#7C3AED",
    bg: "#F5F3FF",
    tags: ["Node.js", "Express", "REST/GraphQL"],
  },
  {
    icon: <ShoppingCart size={24} />,
    title: "E-Commerce Systems",
    desc: "Polished online storefronts with secure checkouts, payment gateway integrations (Stripe, Razorpay, etc.), inventory management, and tailored admin dashboards.",
    color: "#EC4899",
    bg: "#FDF2F8",
    tags: ["Custom Cart", "Payment APIs", "Shopify"],
  },
  {
    icon: <Layers size={24} />,
    title: "SaaS & Custom Web Apps",
    desc: "Full-scale software-as-a-service (SaaS) products, interactive client portals, business automation tools, and real-time database management dashboards.",
    color: "#10B981",
    bg: "#ECFDF5",
    tags: ["Multi-tenant", "Dashboards", "SQL/NoSQL"],
  },
  {
    icon: <FileText size={24} />,
    title: "CMS & Corporate Portals",
    desc: "Secure, easily-manageable content management systems (WordPress, Strapi, Sanity) enabling non-technical teams to update site copy and media instantly.",
    color: "#F59E0B",
    bg: "#FFFBEB",
    tags: ["WordPress", "Headless CMS", "Sanity"],
  },
  {
    icon: <Search size={24} />,
    title: "Performance & SEO Tuning",
    desc: "Page speed enhancement, Core Web Vitals optimization, semantic HTML setups, metadata architectures, and comprehensive technical audits for peak search visibility.",
    color: "#06B6D4",
    bg: "#ECFEFF",
    tags: ["Web Vitals", "Speed Audit", "SEO Setup"],
  },
];

const lifecycleSteps = [
  {
    num: "01",
    icon: <Search size={24} />,
    title: "Requirement & Blueprint",
    desc: "Discovery sessions to define project scope, map features, choose tech architecture, and create structured wireframes.",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  {
    num: "02",
    icon: <Laptop size={24} />,
    title: "UI/UX Prototyping",
    desc: "Designing high-fidelity UI screens, interactive mockups, and custom style systems tailored to match your brand identity.",
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    num: "03",
    icon: <Code2 size={24} />,
    title: "Agile Development",
    desc: "Writing clean, commented, modular code. Regular sprint demo updates with full integration of databases and cloud platforms.",
    color: "#10B981",
    bg: "#ECFDF5",
  },
  {
    num: "04",
    icon: <Rocket size={24} />,
    title: "QA, Deploy & Handover",
    desc: "Rigorous testing across screen dimensions and platforms. Deployment to secure cloud hosting with thorough training and documentation.",
    color: "#F59E0B",
    bg: "#FFFBEB",
  },
];

const techStack = [
  {
    category: "Frontend",
    items: [
      { name: "React.js", icon: <Laptop size={18} /> },
      { name: "Next.js", icon: <Code2 size={18} /> },
      { name: "JavaScript (ES6)", icon: <Terminal size={18} /> },
      { name: "HTML5 & CSS3", icon: <Layers size={18} /> },
    ],
  },
  {
    category: "Backend & Databases",
    items: [
      { name: "Node.js & Express", icon: <Server size={18} /> },
      { name: "Python & Django", icon: <Terminal size={18} /> },
      { name: "MongoDB", icon: <Database size={18} /> },
      { name: "PostgreSQL & MySQL", icon: <Database size={18} /> },
    ],
  },
  {
    category: "Cloud, DevOps & CMS",
    items: [
      { name: "AWS Cloud Hosting", icon: <Cloud size={18} /> },
      { name: "Docker", icon: <Settings size={18} /> },
      { name: "WordPress & Strapi", icon: <FileText size={18} /> },
      { name: "Vercel & Netlify", icon: <Cloud size={18} /> },
    ],
  },
];

const faqs = [
  {
    q: "What technologies do you use for web development?",
    a: "We primary develop custom apps using the MERN stack (MongoDB, Express, React, Node.js) and Next.js for high-performance frontend interfaces. For content-driven sites, we implement WordPress or headless architectures like Strapi/Sanity paired with React.",
  },
  {
    q: "Will my website be mobile-friendly and responsive?",
    a: "Absolutely. Every web application and website we design is fully responsive. We build and test layouts across all device resolutions — from smartphones to wide screen monitors — to ensure perfect visual appeal and user experience.",
  },
  {
    q: "Do you handle domain registration and hosting setups?",
    a: "Yes. While you retain full ownership of the accounts, our DevOps team helps you set up domain names, purchase web hosting (AWS, Vercel, Hostinger, etc.), install SSL security certificates, and deploy the application.",
  },
  {
    q: "Do you provide post-launch maintenance and support?",
    a: "Yes, we offer flexible post-launch support programs. This covers monthly security patches, library updates, content modifications, database backups, performance monitoring, and bug fixing to keep your platform running smoothly.",
  },
  {
    q: "Is SEO optimization included in your web development service?",
    a: "We integrate technical SEO best practices into every build. This includes creating search-engine-friendly clean HTML tags, optimized site hierarchies, fast page speeds, custom metadata fields, XML sitemaps, and robots.txt configurations.",
  },
];

// ─── COMPONENT DEFINITION ────────────────────────────────────────────────────

export default function WebDevelopment() {
  const [openFaq, setOpenFaq] = useState(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("wd-shown"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <div className="wd-page">

      {/* ======================================================
          SECTION 1 — HERO
      ====================================================== */}
      <section className="wd-hero">
        <div className="wd-hero-bg-pattern" />
        <div className="wd-hero-bg-gradient" />

        <div className="wd-container">
          <div className="wd-hero-layout">
            {/* Hero Left Content */}
            <div className="wd-hero-content">
              <div className="wd-hero-pill">
                <span className="wd-hero-pill-dot" />
                IT Services &nbsp;·&nbsp; Custom Web Development
              </div>

              <h1>
                Custom Web Apps &amp; <br />
                <span className="wd-highlight">Digital Portals</span> Built for Performance
              </h1>

              <p className="wd-hero-desc">
                EduITalent delivers bespoke frontend applications, server backend engineering,
                and conversion-driven e-commerce setups. We build clean-coded, secure,
                and fast digital products tailored specifically to scale your business.
              </p>

              <div className="wd-hero-check-list">
                {["Next.js & React Architectures", "Optimized Web Performance", "Statutory Compliance Compliant Systems"].map((item, idx) => (
                  <div key={idx} className="wd-hero-check-item">
                    <CheckCircle2 size={16} className="wd-check-icon-blue" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="wd-hero-btns">
                <Link to="/contact" className="wd-btn-primary">
                  Start Your Project <ArrowRight size={16} />
                </Link>
                <a href="#wd-tech-stack" className="wd-btn-ghost">
                  <Play size={12} fill="currentColor" className="wd-play-icon" /> View Tech Stack
                </a>
              </div>
            </div>

            {/* Hero Right Visual (Browser Mock) */}
            <div className="wd-hero-right">
              <div className="wd-browser-mock animate-float">
                <div className="wd-browser-bar">
                  <div className="wd-browser-dots">
                    <span className="dot dot-red" />
                    <span className="dot dot-yellow" />
                    <span className="dot dot-green" />
                  </div>
                  <div className="wd-browser-url">https://eduitalent.com/web-development</div>
                </div>
                <div className="wd-browser-content">
                  <div className="wd-code-area">
                    <div className="wd-code-line"><span className="line-num">1</span> <span className="keyword">import</span> React <span className="keyword">from</span> <span className="string">'react'</span>;</div>
                    <div className="wd-code-line"><span className="line-num">2</span> <span className="keyword">const</span> WebApp = () =&gt; &#123;</div>
                    <div className="wd-code-line"><span className="line-num">3</span> &nbsp;&nbsp;<span className="keyword">return</span> (</div>
                    <div className="wd-code-line"><span className="line-num">4</span> &nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className="tag">div</span> className=<span className="string">"scalable-platform"</span>&gt;</div>
                    <div className="wd-code-line"><span className="line-num">5</span> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className="tag">Header</span> brand=<span className="string">"EduITalent"</span> /&gt;</div>
                    <div className="wd-code-line"><span className="line-num">6</span> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;<span className="tag">PerformanceOptimize</span> coreVitals=<span className="string">"100%"</span> /&gt;</div>
                    <div className="wd-code-line"><span className="line-num">7</span> &nbsp;&nbsp;&nbsp;&nbsp;&lt;/<span className="tag">div</span>&gt;</div>
                    <div className="wd-code-line"><span className="line-num">8</span> &nbsp;&nbsp;);</div>
                    <div className="wd-code-line"><span className="line-num">9</span> &#125;;</div>
                    <div className="wd-code-line"><span className="line-num">10</span> <span className="keyword">export default</span> WebApp;</div>
                  </div>

                  <div className="wd-browser-preview">
                    <div className="wd-preview-nav">Preview Window</div>
                    <div className="wd-preview-body">
                      <div className="wd-preview-row">
                        <div className="wd-preview-block accent" />
                        <div className="wd-preview-block" />
                      </div>
                      <div className="wd-preview-row">
                        <div className="wd-preview-block wide" />
                        <div className="wd-preview-block" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 2 — STATS BAR
      ====================================================== */}
      <section className="wd-stats-bar">
        <div className="wd-container">
          <div className="wd-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="wd-stats-item">
                <div className="wd-stats-icon" style={{ color: s.color, background: `${s.color}14` }}>
                  {s.icon}
                </div>
                <div>
                  <div className="wd-stats-value" style={{ color: s.color }}>{s.value}</div>
                  <div className="wd-stats-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 3 — INNER SERVICES GRID
      ====================================================== */}
      <section className="wd-services-section">
        <div className="wd-container">
          <div className="wd-section-hdr-center wd-reveal" ref={addReveal}>
            <span className="wd-badge-lbl">Web Services</span>
            <h2>Core Web Development Specialisations</h2>
            <p>End-to-end frontend, backend, and full-stack solutions built around your business goals.</p>
          </div>

          <div className="wd-services-grid">
            {innerServices.map((svc, i) => (
              <div className="wd-svc-card wd-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="wd-svc-icon-box" style={{ background: svc.bg, color: svc.color }}>
                  {svc.icon}
                </div>
                <h3>{svc.title}</h3>
                <p>{svc.desc}</p>
                <div className="wd-svc-tags">
                  {svc.tags.map((tag, idx) => (
                    <span key={idx} className="wd-svc-tag" style={{ background: svc.bg, color: svc.color }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 4 — DEVELOPMENT PROCESS
      ====================================================== */}
      <section className="wd-process-section">
        <div className="wd-container">
          <div className="wd-section-hdr-center wd-reveal" ref={addReveal}>
            <span className="wd-badge-lbl">Our Process</span>
            <h2>How We Build Custom Websites</h2>
            <p>A structured, agile development pipeline ensuring transparency, speed, and flawless code execution.</p>
          </div>

          <div className="wd-process-grid">
            {lifecycleSteps.map((step, i) => (
              <div className="wd-process-card wd-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.1}s` }}>
                <span className="wd-proc-step-num">{step.num}</span>
                <div className="wd-proc-icon-box" style={{ background: step.bg, color: step.color }}>
                  {step.icon}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < lifecycleSteps.length - 1 && <div className="wd-proc-connector">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 5 — TECH STACK
      ====================================================== */}
      <section className="wd-tech-section" id="wd-tech-stack">
        <div className="wd-container">
          <div className="wd-section-hdr-center wd-reveal" ref={addReveal}>
            <span className="wd-badge-lbl-purple">Technologies</span>
            <h2>Our Custom Web Development Stack</h2>
            <p>We leverage modern, secure, and industry-standard frameworks to build robust web systems.</p>
          </div>

          <div className="wd-tech-grid">
            {techStack.map((cat, i) => (
              <div className="wd-tech-card wd-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.12}s` }}>
                <h3>{cat.category}</h3>
                <div className="wd-tech-items">
                  {cat.items.map((tech, idx) => (
                    <div key={idx} className="wd-tech-item">
                      <div className="wd-tech-item-icon">{tech.icon}</div>
                      <span>{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 6 — FAQ
      ====================================================== */}
      <section className="wd-faq-section">
        <div className="wd-container">
          <div className="wd-section-hdr-center wd-reveal" ref={addReveal}>
            <span className="wd-badge-lbl">FAQ</span>
            <h2>Web Development FAQ</h2>
            <p>Find answers to common questions about our web design, technical scope, and project management.</p>
          </div>

          <div className="wd-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`wd-faq-item ${openFaq === i ? "wd-faq-open" : ""}`}>
                <button
                  className="wd-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="wd-faq-num">Q{i + 1}</span>
                  <span className="wd-faq-q-text">{faq.q}</span>
                  <span className="wd-faq-chevron">
                    <ChevronDown size={18} style={{ transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="wd-faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 7 — CTA BANNER
      ====================================================== */}
      <section className="wd-cta-banner">
        <div className="wd-cta-banner-bg" />
        <div className="wd-container">
          <div className="wd-cta-banner-inner">
            <div className="wd-cta-banner-content">
              <h2>Ready to Build Your Custom Platform?</h2>
              <p>
                Get in touch today for a free web project assessment and discovery blueprint.
                Let's discuss how we can engineer the ideal solution for your organization.
              </p>
              <div className="wd-cta-banner-btns">
                <Link to="/contact" className="wd-cta-white-btn">
                  Get Free Consultation <ArrowRight size={16} />
                </Link>
                <a href={`tel:${companyInfo.phone}`} className="wd-cta-ghost-btn">
                  <Phone size={14} /> Call Us Now
                </a>
              </div>
            </div>
            <div className="wd-cta-contact-cards">
              <a href={`tel:${companyInfo.phone}`} className="wd-cta-contact-card">
                <div className="wd-cta-contact-icon"><Phone size={16} /></div>
                <div>
                  <div className="wd-cta-contact-label">India Office</div>
                  <div className="wd-cta-contact-val">{companyInfo.phone}</div>
                </div>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="wd-cta-contact-card">
                <div className="wd-cta-contact-icon"><Send size={16} /></div>
                <div>
                  <div className="wd-cta-contact-label">Email Us</div>
                  <div className="wd-cta-contact-val">{companyInfo.email}</div>
                </div>
              </a>
              <a href={`tel:${companyInfo.phone2}`} className="wd-cta-contact-card">
                <div className="wd-cta-contact-icon"><Phone size={16} /></div>
                <div>
                  <div className="wd-cta-contact-label">Globe Office</div>
                  <div className="wd-cta-contact-val">{companyInfo.phone2}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
